import type { Trip } from '@transp/shared'
import { type JourneyPosition, journeyPositionAt } from './position'

/**
 * Abaixo desta velocidade o onibus e tratado como PARADO.
 *
 * Nao e limiar de conveniencia: com cadencia de 60 s e ruido tipico de GPS urbano de
 * algumas dezenas de metros, um veiculo imovel aparenta se mexer alguns metros por
 * amostra. Sobre a janela de 90 s abaixo, 3 km/h correspondem a 75 m, acima do ruido e
 * bem abaixo de qualquer deslocamento real em transito.
 */
export const STOPPED_KMH = 3

/**
 * Janela para estimar velocidade. Curta demais mede ruido de GPS; longa demais borra a
 * partida e a chegada. 90 s cobre uma amostra e meia da cadencia de 60 s.
 */
const SPEED_WINDOW_S = 90

export type Bus = JourneyPosition & {
	tripId: number
	vehicle: number
	/** Velocidade estimada sobre a janela, ou null quando nao ha par de amostras util. */
	kmh: number | null
	/** Parado agora. Null quando a velocidade nao pode ser estimada. */
	stopped: boolean | null
}

/** Segmentos agrupados por viagem. A viagem e a unidade: um onibus e um so. */
export function segmentsByTrip(trips: Trip[]): Map<number, Trip[]> {
	const by = new Map<number, Trip[]>()
	for (const trip of trips) {
		const list = by.get(trip.tripId)
		if (list) list.push(trip)
		else by.set(trip.tripId, [trip])
	}
	return by
}

/** Onibus em curso no instante t, um por VIAGEM, cada um na sua posicao real. */
export function busesAt(trips: Trip[], t: number): Bus[] {
	const out: Bus[] = []
	for (const [tripId, segments] of segmentsByTrip(trips)) {
		const p = journeyPositionAt(segments, t)
		if (!p) continue
		const kmh = speedAt(segments, t, p)
		out.push({
			...p,
			tripId,
			vehicle: segments[0].vehicle,
			kmh,
			stopped: kmh === null ? null : kmh < STOPPED_KMH,
		})
	}
	// Ordem estavel: a tela nao pode reordenar a pilha de desenho de um quadro para o
	// outro so porque o Map iterou diferente.
	out.sort((a, b) => a.tripId - b.tripId)
	return out
}

/**
 * Velocidade sobre a janela, olhando para tras e, se nao der, para a frente.
 *
 * Posicao MANTIDA (buraco no dado) nao entra: durante o buraco nao sabemos onde o
 * onibus estava, e comparar contra uma posicao congelada produziria zero km/h falso,
 * que a tela mostraria como "parado".
 */
function speedAt(segments: Trip[], t: number, now: JourneyPosition): number | null {
	if (now.held) return null
	for (const dt of [-SPEED_WINDOW_S, SPEED_WINDOW_S]) {
		const other = journeyPositionAt(segments, t + dt)
		if (!other || other.held) continue
		const m = metersBetween(now.position, other.position)
		return (m / SPEED_WINDOW_S) * 3.6
	}
	return null
}

export function metersBetween(a: [number, number], b: [number, number]): number {
	const dx = (b[0] - a[0]) * 111320 * Math.cos((a[1] * Math.PI) / 180)
	const dy = (b[1] - a[1]) * 110540
	return Math.hypot(dx, dy)
}

/**
 * Distancia abaixo da qual dois onibus do MESMO sentido estao em comboio.
 *
 * 250 m a velocidade tipica de corredor (~20 km/h) equivale a cerca de 45 s de
 * intervalo, muito abaixo da mediana medida de 6 a 7 min. E separacao que nao acontece
 * por acaso num servico bem espacado.
 *
 * O SENTIDO nao e detalhe: dois onibus se cruzando em direcoes opostas passam
 * momentaneamente a metros um do outro e nao sao comboio nenhum. Agrupar so por
 * posicao produzia comboio falso a cada cruzamento.
 */
export const CONVOY_RADIUS_M = 250

export type Convoy = {
	/** Ids das viagens que compoem o comboio, na ordem em que foram encontradas. */
	members: number[]
	/** Pares ligados no desenho: [posicao A, posicao B]. */
	links: [[number, number], [number, number]][]
	/** Verdadeiro quando ha onibus das DUAS linhas: e o comboio entre irmas. */
	mixed: boolean
}

/**
 * Comboios no instante: grupos de dois ou mais onibus do mesmo sentido, proximos.
 *
 * Os onibus NAO sao fundidos num marcador so - cada um continua com o seu ponto. Fundir
 * escondia carro (dois no mesmo lugar viravam um) e apagava justamente o que se quer
 * mostrar. Aqui o comboio vira ligacao entre pontos, que soma informacao em vez de
 * trocar uma pela outra.
 *
 * Onibus com posicao MANTIDA fica de fora: nao sabemos onde ele esta, entao afirmar que
 * esta em comboio seria inventar.
 */
export function convoysAt(buses: Bus[]): Convoy[] {
	const eligible = buses.filter((b) => !b.held)
	const seen = new Array(eligible.length).fill(false)
	const out: Convoy[] = []

	for (let i = 0; i < eligible.length; i++) {
		if (seen[i]) continue
		const group = [i]
		seen[i] = true
		// Fecho transitivo: A perto de B e B perto de C formam um comboio de tres, mesmo
		// que A e C estejam alem do raio. E o que a fila fisica faz.
		for (let g = 0; g < group.length; g++) {
			for (let j = 0; j < eligible.length; j++) {
				if (seen[j]) continue
				if (eligible[j].direction !== eligible[group[g]].direction) continue
				if (metersBetween(eligible[group[g]].position, eligible[j].position) <= CONVOY_RADIUS_M) {
					seen[j] = true
					group.push(j)
				}
			}
		}
		if (group.length < 2) continue

		const members = group.map((k) => eligible[k])
		const links: [[number, number], [number, number]][] = []
		for (let a = 0; a < members.length; a++) {
			for (let b = a + 1; b < members.length; b++) {
				if (metersBetween(members[a].position, members[b].position) <= CONVOY_RADIUS_M) {
					links.push([members[a].position, members[b].position])
				}
			}
		}
		out.push({
			members: members.map((m) => m.tripId),
			links,
			mixed: new Set(members.map((m) => m.line)).size > 1,
		})
	}
	return out
}
